import type { Dispatch } from '@reduxjs/toolkit';
import { createAction } from '@reduxjs/toolkit';
import Cookie from 'js-cookie';
import get from 'lodash/get';
import throttle from 'lodash/throttle';

import {
  logId,
  type GoogleOauthSuccessResponse,
} from 'src/auth/common/components/GoogleIdentityServicesButton/GoogleIdentityServicesButton';
import { authAPI } from 'src/core/api/axios';
import { sessionMaxActivity } from 'src/core/config';
import { SESSION_EXPIRE_LOCAL_STORAGE_KEY } from 'src/core/constants/storage';
import logout from 'src/core/utils/logout';
import { logger } from 'src/utils/datadog-log-wrapper';

import * as types from './actionTypes';
import { LOGIN } from '../../../constants/api';
import type { LoginResult } from '../login';

type LoginStateError = {
  code: string;
  source: 'google' | 'googleIdentityServices' | 'microsoft' | 'server';
  step?: 'sca' | 'scaEmailContinuation' | 'additionalOutOfBandCheck';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
} | null;

export type LoginState = {
  finalizeSca: boolean;
  loadingSource: 'password' | 'google' | 'microsoft' | null;
  error: LoginStateError;
  panelNotInvited:
    | {
        displayed: true;
        companyName: string;
      }
    | {
        displayed: false;
        companyName: null;
      };
};

export const displayPanelLogin = createAction(types.DISPLAY_PANEL_LOGIN);
export const displayPanelRequestInvite = createAction<{ companyName: string }>(
  types.DISPLAY_PANEL_REQUESTINVITE,
);

export const loginLoading = createAction<'password' | 'google' | 'microsoft'>(
  types.LOGIN_LOADING,
);
const loginSuccess = createAction<object>(types.LOGIN_SUCCESS);
export const loginFailure = createAction<LoginStateError>(types.LOGIN_FAILURE);
export const finalizeSca = createAction(types.FINALIZE_SCA);

export const loginWithPassword =
  (payload: { email: string; password: string }) =>
  async (dispatch: Dispatch): Promise<LoginResult> => {
    dispatch(loginLoading('password'));
    logger.info('password - login call', {
      team: 'travel-and-expenses',
      scope: 'auth::password',
      logId,
      deviceId: Cookie.get('spendeskDeviceUUID'),
    });
    try {
      const { data } = await authAPI.post(LOGIN, {
        // eslint-disable-next-line camelcase
        auth_type: 'password',
        username: payload.email,
        password: payload.password,
      });
      logger.info('password - login success', {
        team: 'travel-and-expenses',
        scope: 'auth::password',
        logId,
        deviceId: Cookie.get('spendeskDeviceUUID'),
      });
      dispatch(loginSuccess(data));

      return {
        isLoggedIn: true,
        accessToken: data.accessToken,
        goToCompanySelection: data.hasImpersonationTargets,
      };
    } catch (error) {
      logger.warn('Error during password login', {
        error: error?.message ?? 'Unknown error',
        team: 'travel-and-expenses',
        scope: 'auth',
      });
      return handleLoginError(error, dispatch);
    }
  };

export const loginWithOpenId =
  (payload: { idToken: string; accessToken: string }) =>
  async (dispatch: Dispatch): Promise<LoginResult> => {
    dispatch(loginLoading('microsoft'));

    logger.info('Microsoft - login call', {
      team: 'travel-and-expenses',
      scope: 'auth::microsoft',
      logId,
      deviceId: Cookie.get('spendeskDeviceUUID'),
    });
    try {
      const { data } = await authAPI.post(LOGIN, {
        // eslint-disable-next-line camelcase
        auth_type: 'openid',
        // eslint-disable-next-line camelcase
        id_token: payload.idToken,
        // eslint-disable-next-line camelcase
        access_token: payload.accessToken,
      });
      logger.info('Microsoft - login success', {
        team: 'travel-and-expenses',
        scope: 'auth::microsoft',
        logId,
        deviceId: Cookie.get('spendeskDeviceUUID'),
      });
      dispatch(loginSuccess(data));

      return {
        isLoggedIn: true,
        accessToken: data.accessToken,
        goToCompanySelection: data.hasImpersonationTargets,
      };
    } catch (error) {
      logger.warn('Error during open id login', {
        error: error?.message ?? 'Unknown error',
        team: 'travel-and-expenses',
        scope: 'auth',
      });
      return handleLoginError(error, dispatch);
    }
  };

export const loginWithGoogleIdentityServices =
  (payload: GoogleOauthSuccessResponse) =>
  async (dispatch: Dispatch): Promise<LoginResult> => {
    dispatch(loginLoading('google'));
    logger.info('Google OAuth2 - login call', {
      team: 'travel-and-expenses',
      scope: 'auth::google',
      logId,
      deviceId: Cookie.get('spendeskDeviceUUID'),
    });
    try {
      const { data } = await authAPI.post(LOGIN, {
        // eslint-disable-next-line camelcase
        auth_type: 'googleIdentityServices',
        code: payload.code,
        redirectUrl: payload.redirectUrl,
      });
      logger.info('Google OAuth2 - login success', {
        team: 'travel-and-expenses',
        scope: 'auth::google',
        logId,
        deviceId: Cookie.get('spendeskDeviceUUID'),
      });
      dispatch(loginSuccess(data));

      return {
        isLoggedIn: true,
        accessToken: data.accessToken,
        goToCompanySelection: data.hasImpersonationTargets,
      };
    } catch (error) {
      logger.warn(
        'Google OAuth2 - Error during Google Identity Services login',
        {
          error: error?.message ?? 'Unknown error',
          team: 'travel-and-expenses',
          scope: 'auth',
          deviceId: Cookie.get('spendeskDeviceUUID'),
        },
      );
      return handleLoginError(error, dispatch);
    }
  };

export const loginWithSca =
  (scaToken: string, redirectUrl?: string) =>
  async (dispatch: Dispatch): Promise<LoginResult> => {
    logger.info('sca - login call', {
      team: 'travel-and-expenses',
      scope: 'auth::sca',
      logId,
      deviceId: Cookie.get('spendeskDeviceUUID'),
    });
    try {
      const { data } = await authAPI.post(LOGIN, {
        // eslint-disable-next-line camelcase
        auth_type: 'sca',
        token: scaToken,
        redirectUrl,
      });
      logger.info('sca - login success', {
        team: 'travel-and-expenses',
        scope: 'auth::sca',
        logId,
        deviceId: Cookie.get('spendeskDeviceUUID'),
      });
      dispatch(loginSuccess(data));

      return {
        isLoggedIn: true,
        accessToken: data.accessToken,
        goToCompanySelection: data.hasImpersonationTargets,
      };
    } catch (error) {
      logger.warn('Error during sca login', {
        error: error?.message ?? 'Unknown error',
        team: 'travel-and-expenses',
        scope: 'auth',
      });
      return handleLoginError(error, dispatch);
    }
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleLoginError = (error: any, dispatch: Dispatch): LoginResult => {
  const data = get(error, 'response.data', { error: 'unknown_error' });
  if (data?.step === 'additionalOutOfBandCheck') {
    dispatch(finalizeSca());
  }
  if (data.error === 'existing_domain') {
    dispatch(displayPanelRequestInvite({ companyName: data.data.companyName }));
  } else {
    dispatch(loginFailure({ code: data.error, source: 'server', data }));
  }

  if (error.isAxiosError) {
    return { isLoggedIn: false, data };
  }

  throw error;
};

const killSession = async (): Promise<void> => {
  await logout();

  logger.info(
    'Redirect to login page with session-expired query param - killSession',
    {
      team: 'travel-and-expenses',
      scope: 'auth',
      currentPage: window.location.href,
    },
  );

  window.location.href = `/auth/login?session-expired=1&targetUrl=${encodeURIComponent(
    window.location.href,
  )}`;
};

const setLastActivity = throttle(
  () => {
    localStorage.setItem(
      SESSION_EXPIRE_LOCAL_STORAGE_KEY,
      Date.now().toString(),
    );
  },
  5000,
  { leading: true, trailing: false },
);

const getLastActivity = (): Date | undefined => {
  const lastActivity = localStorage.getItem(SESSION_EXPIRE_LOCAL_STORAGE_KEY);
  if (lastActivity) {
    return new Date(Number.parseInt(lastActivity));
  }
};

let autoLogoutTimer: number | undefined;
export const automaticSessionExpire = () => async (): Promise<void> => {
  // update the value on login so that we don't disconnect after 45s
  setLastActivity();

  window.addEventListener('mousemove', setLastActivity);
  window.addEventListener('keypress', setLastActivity);

  if (autoLogoutTimer) {
    return;
  }

  autoLogoutTimer = window.setInterval(async () => {
    const now = Date.now();
    const lastActivity = getLastActivity();
    const inactivity = lastActivity ? now - lastActivity.getTime() : undefined;

    if (inactivity && inactivity > sessionMaxActivity) {
      killSession();
    }
  }, sessionMaxActivity / 2);
};
